import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, ButtonBase, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { useColorMode } from '../../../../../context/colorMode/colorMode.context';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import AssessIntroImage from '../../../../../assets/images/Assess-Intro.png';
import RazProgressSVG from '../../../../../components/UI/SVG_IMGs/RazProgressSVG';

const ACCENT = 'var(--brand-primary, #ED6628)';

// ── ui_rules (ported from my-dq-web's AssessmentSlide) ─────────────────────
// Question/answer data can carry a `ui_rules` array that layers extra
// behavior onto a question without a new question TYPE:
//   INDEX_EFFECT / USER_INPUT — that option index is an inline text field
//     instead of a clickable row.
//   INDEX_EFFECT / UNSELECT   — selecting that option (MAQ) clears every
//     other pick, and picking anything else clears it.
//   QUESTION_EFFECT / TOOLTIP — wraps a keyword in the question text with a
//     hoverable tooltip.
//   ANSWER_EFFECT / TOOLTIP   — same, but for a keyword inside choice text.
const parseUiRules = (rawRules) => {
    if (!rawRules) return [];
    try {
        const value = typeof rawRules === 'string' ? JSON.parse(rawRules) : rawRules;
        return Array.isArray(value) ? value : [];
    } catch (e) {
        console.error('Failed to parse ui_rules JSON:', rawRules, e);
        return [];
    }
};

// Splits `text` (a plain string, or an already-segmented array from a prior
// rule) into a new segment array, replacing every case-insensitive match of
// rule.text with a `{ tooltip, label }` marker. Building segments directly
// (rather than an HTML string later parsed by a library) avoids needing an
// HTML-parsing dependency dq-seal-client doesn't otherwise have.
const applyTooltipRule = (text, rule) => {
    if (!rule?.text) return Array.isArray(text) ? text : [text];
    const segments = Array.isArray(text) ? text : [text];
    const needle = rule.text.toLowerCase();
    const result = [];
    segments.forEach((seg) => {
        if (typeof seg !== 'string' || !seg) { if (seg) result.push(seg); return; }
        const lower = seg.toLowerCase();
        let cursor = 0;
        let idx = lower.indexOf(needle, cursor);
        while (idx !== -1) {
            if (idx > cursor) result.push(seg.slice(cursor, idx));
            result.push({ tooltip: rule.html, label: seg.slice(idx, idx + rule.text.length) });
            cursor = idx + rule.text.length;
            idx = lower.indexOf(needle, cursor);
        }
        if (cursor < seg.length) result.push(seg.slice(cursor));
    });
    return result;
};

const useStyles = makeStyles((theme) => ({
    navButton: {
        backgroundColor: `${ACCENT} !important`,
        width: '54px !important',
        height: '54px !important',
        borderRadius: '50% !important',
        boxShadow: '0 2px 8px rgba(237,102,40,0.3) !important',
        flexShrink: 0,
        '&:focus': { outline: 'none', border: 'none', boxShadow: '0 2px 8px rgba(237,102,40,0.3) !important' },
        '&.Mui-disabled': { opacity: 0.3 },
    },
    navIcon: {
        color: '#fff !important',
        fontSize: '20px !important',
    },
}));

const mapRawItems = (rawItems) => {
    // The backend now returns the final order: questions grouped into DQ-competency
    // sections (randomized within each section + section order), each preceded by a
    // `section_intro` marker, with the assessment_intro card at the very front. We
    // render that order as-is — no client-side shuffling.
    const mapped = (rawItems || []).map((item, index) => {
        if (item.type === 'assessment_intro') {
            return {
                id: index + 1,
                type: 'assessment_intro',
                title: item.title || '',
                image_url: item.image_url || '',
                content: item.content || '',
                assessment_type: item.assessment_type || '',
                layout: item.layout || '',
            };
        }
        if (item.type === 'section_intro') {
            return {
                id: index + 1,
                type: 'section_intro',
                section_code: item.section_code || '',
                title: item.title || '',
                content: item.description || '',
            };
        }
        let options = [];
        try {
            options = typeof item.instrument_response === 'string'
                ? JSON.parse(item.instrument_response)
                : item.instrument_response;
            if (!Array.isArray(options)) options = [options];
        } catch (_) {
            options = [];
        }
        const type = item.category_11 || 'MCQ';
        return {
            id: index + 1,
            type,
            instrument_question: item.instrument_question || '',
            options,
            ui_rules: item.ui_rules,
            question_subtitle_top: item.question_subtitle_top || '',
            question_subtitle_bottom: item.question_subtitle_bottom || '',
            // MTQ (ranking/ordering): the answer IS an order, so it starts as the
            // options' own indices in their given order (matching my-dq-web) rather
            // than an empty/null selection — reordering moves indices around, the
            // option TEXT is looked up by index wherever it's displayed.
            userAnswer: type === 'MAQ' ? [] : type === 'MTQ' ? options.map((_, i) => i) : null,
        };
    });

    return mapped.map((q, i) => ({ ...q, id: i + 1 }));
};

const AssessmentTab = ({ programData, modules = [] }) => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const [assessType, setAssessType] = useState('pre');
    const [currentIdx, setCurrentIdx] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);

    // Build grouped structure: [{ moduleTitle, sessions: [{ key, sessionTitle, data }] }]
    // Falls back to flat list if modules aren't available
    const groupedModules = useMemo(() => {
        if (!programData?.session_assessments_by_language) return [];
        const byLang = programData.session_assessments_by_language;
        const firstLang = Object.keys(byLang)[0] || 'en-US';
        const sessMap = byLang[firstLang] || {};

        const hasAssessment = (key) => {
            const s = sessMap[key] || {};
            return (Array.isArray(s.pre) && s.pre.length) || (Array.isArray(s.post) && s.post.length);
        };

        // Try to group using the modules prop
        if (modules && modules.length > 0) {
            const groups = [];
            modules.forEach((mod) => {
                const sessions = [];
                (mod.units || []).forEach((unit) => {
                    (unit.sessions || []).forEach((sess) => {
                        if (sess.session_code && hasAssessment(sess.session_code)) {
                            sessions.push({
                                key: sess.session_code,
                                sessionTitle: sess.session_title || sess.session_code,
                                data: sessMap[sess.session_code],
                            });
                        }
                    });
                });
                if (sessions.length > 0) {
                    groups.push({ moduleTitle: mod.module_title || 'Module', sessions });
                }
            });
            if (groups.length > 0) return groups;
        }

        // Fallback: flat list under one group
        const flatSessions = Object.keys(sessMap)
            .filter(hasAssessment)
            .map((key, idx) => ({ key, sessionTitle: `Session ${idx + 1}`, data: sessMap[key] }));
        return flatSessions.length > 0 ? [{ moduleTitle: null, sessions: flatSessions }] : [];
    }, [programData, modules]);

    // Flat list of all available sessions (for the switcher tabs + single-session auto-select)
    const availableSessions = useMemo(() => groupedModules.flatMap(g => g.sessions), [groupedModules]);

    // Auto-select when there's only one session
    useEffect(() => {
        if (availableSessions.length === 1 && !selectedSession) {
            setSelectedSession(availableSessions[0].key);
        }
    }, [availableSessions, selectedSession]);

    const { preItems, postItems } = useMemo(() => {
        if (!programData?.session_assessments_by_language || !selectedSession)
            return { preItems: [], postItems: [] };
        const byLang = programData.session_assessments_by_language;
        const firstLang = Object.keys(byLang)[0] || 'en-US';
        const sessMap = byLang[firstLang] || {};
        const sess = sessMap[selectedSession] || {};
        return {
            preItems: Array.isArray(sess.pre) ? sess.pre : [],
            postItems: Array.isArray(sess.post) ? sess.post : [],
        };
    }, [programData, selectedSession]);

    useEffect(() => {
        const raw = assessType === 'pre' ? preItems : postItems;
        setQuestions(mapRawItems(raw));
        setCurrentIdx(0);
    }, [assessType, preItems, postItems]);

    const hasAssessments = availableSessions.length > 0;
    const total = questions.length;
    const currentQuestion = questions[currentIdx] || null;

    // ui_rules: current question's parsed rules, the option indices they flag
    // as a custom-input field, and floating-tooltip state for the
    // QUESTION_EFFECT/ANSWER_EFFECT rules. UNSELECT-rule indices are
    // recomputed fresh inside handleOptionSelect's setQuestions updater
    // instead of memoized here, so they always match the specific question
    // object React is updating rather than whatever was in scope this render.
    const uiRules = useMemo(() => parseUiRules(currentQuestion?.ui_rules), [currentQuestion]);
    const inputIndices = useMemo(
        () => uiRules.filter(r => r.type === 'INDEX_EFFECT' && r.effect === 'USER_INPUT').map(r => r.index),
        [uiRules]
    );
    const processedQuestionSegments = useMemo(() => {
        const base = currentQuestion?.instrument_question || '';
        return uiRules
            .filter(r => r.type === 'QUESTION_EFFECT' && r.effect === 'TOOLTIP')
            .reduce((text, rule) => applyTooltipRule(text, rule), base);
    }, [currentQuestion, uiRules]);
    const processedChoiceSegments = useMemo(() => {
        const options = currentQuestion?.options || [];
        const tooltipRules = uiRules.filter(r => r.type === 'ANSWER_EFFECT' && r.effect === 'TOOLTIP');
        return options.map(option => tooltipRules.reduce((text, rule) => applyTooltipRule(text, rule), option));
    }, [currentQuestion, uiRules]);

    const [tooltipText, setTooltipText] = useState('');
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const onHoverTooltip = (event, html) => {
        setTooltipPos({ x: event.clientX, y: event.clientY });
        setTooltipText(html || '');
        setDisplayTooltip(true);
    };
    const onExitHoverTooltip = () => setDisplayTooltip(false);

    // Renders a plain string or a tooltip-segment array (see applyTooltipRule)
    // as React nodes — plain text runs through untouched, `{tooltip, label}`
    // markers become a hoverable span that opens the floating tooltip box.
    const renderTextSegments = (value) => {
        const segments = Array.isArray(value) ? value : [value];
        return segments.map((seg, i) => (typeof seg === 'string'
            ? <React.Fragment key={i}>{seg}</React.Fragment>
            : (
                <Box
                    key={i}
                    component="span"
                    onMouseEnter={(e) => onHoverTooltip(e, seg.tooltip)}
                    onMouseLeave={onExitHoverTooltip}
                    sx={{ textDecoration: 'underline dotted', textUnderlineOffset: '3px', cursor: 'help', color: ACCENT, fontWeight: 700 }}
                >
                    {seg.label}
                </Box>
            )
        ));
    };

    // `questions` also holds assessment_intro/section_intro cards, which aren't
    // real questions — the "Question NN of total" display must count (and
    // number) only the actual questions, not those intro slides.
    const realQuestionTotal = questions.filter(q => q.type !== 'assessment_intro' && q.type !== 'section_intro').length;
    const realQuestionNumber = questions
        .slice(0, currentIdx + 1)
        .filter(q => q.type !== 'assessment_intro' && q.type !== 'section_intro').length;

    // "None of the above" / "all of the above" are exclusive within a MAQ:
    // picking one clears every other pick, and picking anything else clears
    // any exclusive option already held.
    const isExclusiveMaqOption = (option) => /none of the above|all of the above/i.test(String(option || ''));

    // `optionIndex` is only used for the rule-driven MAQ unselect check below —
    // everything else still identifies options by value, unchanged.
    const handleOptionSelect = (option, optionIndex) => {
        setQuestions((prev) =>
            prev.map((q, i) => {
                if (i !== currentIdx) return q;
                const updated = { ...q };
                if (q.type === 'MCQ' || q.type === 'LSQ' || q.type === 'YNQ') {
                    // Clicking the already-selected option deselects it.
                    updated.userAnswer = q.userAnswer === option ? null : option;
                } else if (q.type === 'MAQ') {
                    const prevAnswer = Array.isArray(updated.userAnswer) ? updated.userAnswer : [];
                    const rules = parseUiRules(q.ui_rules);
                    if (prevAnswer.includes(option)) {
                        updated.userAnswer = prevAnswer.filter((o) => o !== option);
                    } else if (rules.length > 0) {
                        // Rule-driven: an UNSELECT-flagged option clears every other
                        // pick; picking anything else clears any UNSELECT option held.
                        const unselectRuleIndices = rules
                            .filter(r => r.type === 'INDEX_EFFECT' && r.effect === 'UNSELECT')
                            .map(r => r.index);
                        if (unselectRuleIndices.includes(optionIndex)) {
                            updated.userAnswer = [option];
                        } else {
                            const optionsList = q.options || [];
                            updated.userAnswer = [
                                ...prevAnswer.filter((o) => !unselectRuleIndices.includes(optionsList.indexOf(o))),
                                option,
                            ];
                        }
                    } else if (isExclusiveMaqOption(option)) {
                        updated.userAnswer = [option];
                    } else {
                        updated.userAnswer = [...prevAnswer.filter((o) => !isExclusiveMaqOption(o)), option];
                    }
                }
                return updated;
            })
        );
    };

    const handleFreeTextChange = (value) => {
        setQuestions((prev) => prev.map((q, i) => (i === currentIdx ? { ...q, userAnswer: value } : q)));
    };

    // ui_rules USER_INPUT: the answer becomes { customInput, selectedIndex },
    // regardless of the question's own type — mirrors my-dq-web exactly.
    const handleCustomInputChange = (optionIndex, text) => {
        setQuestions((prev) => prev.map((q, i) => (
            i === currentIdx
                ? { ...q, userAnswer: { customInput: text, selectedIndex: text ? optionIndex : null } }
                : q
        )));
    };

    const isFreeTextQuestion = (question) => !!question && question.type === 'OPQ' && question.options.includes('Free text');

    const isOptionSelected = (option) => {
        if (!currentQuestion) return false;
        if (['MCQ', 'LSQ', 'YNQ'].includes(currentQuestion.type))
            return currentQuestion.userAnswer === option;
        if (currentQuestion.type === 'MAQ')
            return currentQuestion.userAnswer?.includes(option) || false;
        return false;
    };

    // MTQ: drag-and-drop reordering via pointer events (works for mouse + touch,
    // no DnD library needed). rowRefs holds each row's DOM node so pointermove
    // can tell which row the dragged item is currently over.
    const rowRefs = useRef([]);
    const dragInfo = useRef({ index: null });
    const [dragIndex, setDragIndex] = useState(null);

    const reorderRankedOption = (from, to) => {
        setQuestions((prev) =>
            prev.map((q, i) => {
                if (i !== currentIdx || q.type !== 'MTQ') return q;
                const order = q.userAnswer.slice();
                const [moved] = order.splice(from, 1);
                order.splice(to, 0, moved);
                return { ...q, userAnswer: order };
            })
        );
    };

    const handleDragPointerMove = (e) => {
        const from = dragInfo.current.index;
        if (from == null) return;
        const y = e.clientY;
        const to = rowRefs.current.findIndex((el) => {
            if (!el) return false;
            const rect = el.getBoundingClientRect();
            return y >= rect.top && y <= rect.bottom;
        });
        if (to === -1 || to === from) return;
        reorderRankedOption(from, to);
        dragInfo.current.index = to;
        setDragIndex(to);
    };

    const handleDragPointerUp = () => {
        dragInfo.current.index = null;
        setDragIndex(null);
        window.removeEventListener('pointermove', handleDragPointerMove);
        window.removeEventListener('pointerup', handleDragPointerUp);
    };

    const handleDragPointerDown = (e, index) => {
        e.preventDefault();
        dragInfo.current.index = index;
        setDragIndex(index);
        window.addEventListener('pointermove', handleDragPointerMove);
        window.addEventListener('pointerup', handleDragPointerUp);
    };

    const calculateProgress = () => (realQuestionTotal > 0 ? (realQuestionNumber / realQuestionTotal) * 100 : 0);

    // An option no longer needs to be selected before advancing — Next stays
    // enabled regardless of whether the current question has an answer. The
    // last-question boundary is handled separately, at the call sites.
    const isNextDisabled = () => !currentQuestion;

    // MTQ: ranked/ordered list — drag handle + rank-number badge, reordered by
    // dragging (pointer events), since the answer is the order itself.
    const renderRankingOptions = () => {
        const order = currentQuestion?.userAnswer;
        if (!Array.isArray(order)) return null;
        rowRefs.current = [];

        return (
            <Box sx={{ maxWidth: '920px', mx: 'auto', width: '100%', mt: 3 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-text-muted, #6B7280)', mb: 1 }}>
                    Drag and drop to order
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {order.map((optionIndex, i) => (
                        <Tooltip key={`${i}-${optionIndex}`} title="Drag to reorder" placement="top" arrow>
                            <Box
                                ref={(el) => { rowRefs.current[i] = el; }}
                                onPointerDown={(e) => handleDragPointerDown(e, i)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2,
                                    p: '14px 20px', borderRadius: '12px',
                                    border: '1px solid var(--brand-border, #E5E7EB)',
                                    backgroundColor: 'var(--brand-surface, #ffffff)',
                                    opacity: dragIndex === i ? 0.5 : 1,
                                    boxShadow: dragIndex === i ? '0 4px 14px rgba(0,0,0,0.15)' : 'none',
                                    userSelect: 'none',
                                    cursor: dragIndex === i ? 'grabbing' : 'grab',
                                    touchAction: 'none',
                                    '&:hover': { borderColor: ACCENT },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                    <DragIndicatorIcon />
                                </Box>
                                <Box sx={{
                                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 700, fontSize: 15,
                                    backgroundColor: ACCENT, color: '#ffffff',
                                }}>
                                    {i + 1}
                                </Box>
                                <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500, color: 'var(--brand-text, #111827)', textAlign: 'left' }}>
                                    {currentQuestion.options[optionIndex]}
                                </Typography>
                            </Box>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        );
    };

    // Unified vertical option list — matches the redesigned Assessment Preview
    // (lettered badge + text + checkmark on select), same for every question type.
    const renderOptions = () => {
        if (!currentQuestion?.options) return null;
        if (currentQuestion.type === 'MTQ') return renderRankingOptions();

        if (isFreeTextQuestion(currentQuestion)) {
            return (
                <Box sx={{ maxWidth: '920px', mx: 'auto', width: '100%', mt: 3 }}>
                    <Box
                        component="textarea"
                        value={currentQuestion.userAnswer || ''}
                        onChange={(e) => handleFreeTextChange(e.target.value)}
                        placeholder="Type your answer here..."
                        sx={{
                            width: '100%', minHeight: 140, p: '14px 20px', borderRadius: '12px',
                            border: '1px solid var(--brand-border, #E5E7EB)',
                            backgroundColor: 'var(--brand-surface, #ffffff)',
                            fontSize: 16, fontFamily: 'inherit', color: 'var(--brand-text, #111827)',
                            resize: 'vertical', outline: 'none',
                            '&:focus': { borderColor: ACCENT },
                        }}
                    />
                </Box>
            );
        }

        const { options } = currentQuestion;
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

        return (
            <Box sx={{ maxWidth: '920px', mx: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}>
                {options.map((option, i) => {
                    // ui_rules USER_INPUT: an inline text field instead of a
                    // clickable option row, for this one option index.
                    if (inputIndices.includes(i)) {
                        const isCustomSelected = typeof currentQuestion.userAnswer === 'object'
                            && currentQuestion.userAnswer !== null
                            && currentQuestion.userAnswer.selectedIndex === i;
                        return (
                            <Box
                                key={i}
                                component="input"
                                value={isCustomSelected ? (currentQuestion.userAnswer.customInput || '') : ''}
                                onChange={(e) => handleCustomInputChange(i, e.target.value)}
                                placeholder={option}
                                sx={{
                                    width: '100%', p: '14px 20px', borderRadius: '12px',
                                    border: isCustomSelected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                    backgroundColor: 'var(--brand-surface, #ffffff)',
                                    fontSize: 16, fontFamily: 'inherit', color: 'var(--brand-text, #111827)',
                                    outline: 'none',
                                    '&:focus': { borderColor: ACCENT },
                                }}
                            />
                        );
                    }

                    const selected = isOptionSelected(option);
                    return (
                        <Box
                            key={i}
                            onClick={() => handleOptionSelect(option, i)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 2,
                                p: '14px 20px', borderRadius: '12px', cursor: 'pointer',
                                border: selected ? `2px solid ${ACCENT}` : '1px solid var(--brand-border, #E5E7EB)',
                                backgroundColor: selected ? 'rgba(237,102,40,0.06)' : 'var(--brand-surface, #ffffff)',
                                transition: 'all 0.15s ease',
                                '&:hover': { borderColor: ACCENT, boxShadow: '0px 2px 8px rgba(0,0,0,0.04)' },
                            }}
                        >
                            <Box sx={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: 15,
                                border: selected ? 'none' : '1.5px solid var(--brand-border, #D1D5DB)',
                                backgroundColor: selected ? ACCENT : 'transparent',
                                color: selected ? '#ffffff' : 'var(--brand-text-muted, #6B7280)',
                            }}>
                                {letters[i] || i + 1}
                            </Box>
                            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: selected ? 700 : 500, color: 'var(--brand-text, #111827)', textAlign: 'left' }}>
                                {renderTextSegments(processedChoiceSegments[i] ?? option)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    if (!programData || !hasAssessments) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', opacity: 0.8 }}>
                <CircularProgress size={60} sx={{ mb: 3, color: '#ED6628' }} />
                <Typography variant="h5" style={{ textAlign: 'center', maxWidth: '600px' }}>Preparing your assessments</Typography>
            </Box>
        );
    }

    // Module/session picker — shown when no session is selected yet
    if (!selectedSession) {
        return (
            <Box sx={{ height: '100%', overflowY: 'auto', px: 3, py: 3 }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: 'var(--brand-text, #111827)', mb: 0.5 }}>
                    Assessments
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'var(--brand-text-muted, #9CA3AF)', mb: 3 }}>
                    Choose a session to preview its assessment
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {groupedModules.map((group, gIdx) => (
                        <Box key={gIdx} sx={{
                            border: '1.5px solid var(--brand-border, #EBEBEB)',
                            borderRadius: '16px',
                            backgroundColor: 'var(--brand-surface, #fff)',
                            overflow: 'hidden',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        }}>
                            {/* Module header */}
                            {group.moduleTitle && (
                                <Box sx={{ px: 2.5, py: 1.75, backgroundColor: 'var(--brand-bg, #F9FAFB)', borderBottom: '1px solid var(--brand-border, #F0F0F0)' }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'var(--brand-text, #374151)', letterSpacing: '0.3px' }}>
                                        {group.moduleTitle}
                                    </Typography>
                                </Box>
                            )}

                            {/* Sessions list */}
                            {group.sessions.map((sess, sIdx) => {
                                const preCount = Array.isArray(sess.data?.pre) ? sess.data.pre.length : 0;
                                const postCount = Array.isArray(sess.data?.post) ? sess.data.post.length : 0;
                                return (
                                    <Box key={sess.key} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 2.5,
                                        py: 1.75,
                                        borderBottom: sIdx < group.sessions.length - 1 ? '1px solid var(--brand-border, #F5F5F5)' : 'none',
                                        gap: 2,
                                    }}>
                                        {/* Session number */}
                                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: 'rgba(237,102,40,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#ED6628' }}>
                                                {sIdx + 1}
                                            </Typography>
                                        </Box>

                                        {/* Session info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--brand-text, #111827)', lineHeight: 1.3 }}>
                                                {sess.sessionTitle}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.4 }}>
                                                {preCount > 0 && (
                                                    <Typography sx={{ fontSize: '11px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                        Pre: {preCount}
                                                    </Typography>
                                                )}
                                                {postCount > 0 && (
                                                    <Typography sx={{ fontSize: '11px', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                        Post: {postCount}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Preview button */}
                                        <ButtonBase
                                            onClick={() => { setSelectedSession(sess.key); setAssessType('pre'); setCurrentIdx(0); }}
                                            sx={{
                                                px: 2, py: 0.6,
                                                borderRadius: '20px',
                                                border: '1.5px solid #ED6628',
                                                color: '#ED6628',
                                                fontSize: '12px',
                                                fontWeight: 700,
                                                flexShrink: 0,
                                                transition: 'all 0.15s',
                                                '&:hover': { backgroundColor: '#ED6628', color: '#fff' },
                                            }}
                                        >
                                            Preview
                                        </ButtonBase>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 200px)', px: 2, pt: 0.5, pb: 0.5 }}>
            {displayTooltip && (
                <Box
                    sx={{
                        position: 'fixed', top: tooltipPos.y + 14, left: tooltipPos.x + 14, zIndex: 2000,
                        maxWidth: 320, p: '10px 14px', borderRadius: '8px',
                        backgroundColor: '#1f2937', color: '#fff', fontSize: 13, lineHeight: 1.5,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'none',
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltipText }}
                />
            )}

            {/* Top bar: back button (left) + session title (center) */}
            {availableSessions.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', pt: 2, pb: 0, flexShrink: 0, position: 'relative' }}>
                    <ButtonBase
                        onClick={() => setSelectedSession(null)}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            px: 1.5, py: 0.5, borderRadius: '20px',
                            marginLeft: { xs: '8px', md: '60px' },
                            border: '1.5px solid var(--brand-border, #E5E7EB)',
                            fontSize: '12px', fontWeight: 700, color: 'var(--brand-text-muted, #6B7280)',
                            transition: 'all 0.15s', zIndex: 1,
                            '&:hover': { borderColor: '#ED6628', color: '#ED6628', backgroundColor: 'rgba(237,102,40,0.04)' },
                        }}
                    >
                        <ArrowBackIosIcon style={{ fontSize: 10 }} />
                        All sessions
                    </ButtonBase>
                    <Typography sx={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: { xs: '15px', md: '20px' }, fontWeight: 700, color: 'var(--brand-text, #111827)', pointerEvents: 'none' }}>
                        {availableSessions.find(s => s.key === selectedSession)?.sessionTitle || ''}
                    </Typography>
                </Box>
            )}

            {/* Pre / Post toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', backgroundColor: 'var(--brand-bg, #F3F4F6)', borderRadius: '12px', p: '3px', gap: '3px' }}>
                    {[['pre', 'Pre-Assessment', preItems], ['post', 'Post-Assessment', postItems]].map(([type, label, items]) => (
                        <ButtonBase key={type}
                            onClick={() => setAssessType(type)}
                            disabled={!items.length}
                            sx={{
                                px: 3, py: 0.9,
                                borderRadius: '9px',
                                fontWeight: 700,
                                fontSize: '13px',
                                backgroundColor: assessType === type ? 'var(--brand-surface, #fff)' : 'transparent',
                                color: assessType === type ? '#ED6628' : 'var(--brand-text-muted, #9CA3AF)',
                                boxShadow: assessType === type ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.2s ease',
                                opacity: !items.length ? 0.4 : 1,
                                whiteSpace: 'nowrap',
                            }}
                        >{label}</ButtonBase>
                    ))}
                </Box>
            </Box>

            {/* White card with nav arrows outside (sm+) or overlaid (xs) */}
            <Box sx={{ flex: 1, minHeight: 0, pb: 4, position: 'relative', display: 'flex', alignItems: 'stretch' }}>

                {/* Left nav — outside card on sm+ only */}
                {total > 0 && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', pr: 1.5, flexShrink: 0 }}>
                        <IconButton className={classes.navButton}
                            onClick={() => setCurrentIdx((p) => Math.max(0, p - 1))}
                            disabled={currentIdx === 0}>
                            <ArrowBackIosIcon className={classes.navIcon} />
                        </IconButton>
                    </Box>
                )}

                {/* Card */}
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                }}>
                    {/* Mobile-only overlay arrows (xs) */}
                    {total > 0 && (
                        <>
                            <IconButton
                                className={classes.navButton}
                                onClick={() => setCurrentIdx((p) => Math.max(0, p - 1))}
                                disabled={currentIdx === 0}
                                sx={{ display: { xs: 'flex', sm: 'none' }, position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                            >
                                <ArrowBackIosIcon className={classes.navIcon} />
                            </IconButton>
                            <IconButton
                                className={classes.navButton}
                                onClick={() => setCurrentIdx((p) => Math.min(total - 1, p + 1))}
                                disabled={currentIdx === total - 1 || isNextDisabled()}
                                sx={{ display: { xs: 'flex', sm: 'none' }, position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                            >
                                <ArrowForwardIosIcon className={classes.navIcon} />
                            </IconButton>
                        </>
                    )}

                    {/* Question body */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: (currentQuestion?.type === 'assessment_intro' || currentQuestion?.type === 'section_intro') ? 'center' : 'flex-start', px: { xs: 7, sm: 4 }, py: 1.5 }}>
                        {currentQuestion && (
                            currentQuestion.type === 'section_intro' ? (
                                <Box sx={{ maxWidth: '760px', margin: '0 auto', width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 800, letterSpacing: '1.5px', color: '#ED6628', textTransform: 'uppercase' }}>
                                        Next Section
                                    </Typography>
                                    <Typography variant="h3" style={{ color: '#C91C6E', fontWeight: 700, textAlign: 'center', fontSize: 'clamp(22px, 3.5vw, 34px)', lineHeight: 1.3 }}>
                                        {currentQuestion.title || 'Section'}
                                    </Typography>
                                    {currentQuestion.content && (
                                        <Typography variant="body1" style={{ color: 'var(--brand-text-muted, #555)', fontSize: '17px', lineHeight: 1.6, textAlign: 'center' }}>
                                            {currentQuestion.content}
                                        </Typography>
                                    )}
                                </Box>
                            ) : currentQuestion.type === 'assessment_intro' ? (
                                <Box sx={{ maxWidth: '900px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'center', gap: { xs: 3, md: 4 }, width: '100%', mb: currentQuestion.content ? 3 : 0 }}>
                                        <Box sx={{ flex: { xs: '0 0 auto', md: '0 0 45%' }, maxWidth: { xs: '500px', md: '500px' }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            <Box
                                                component="img"
                                                src={currentQuestion.image_url || AssessIntroImage}
                                                alt={currentQuestion.title || 'Assessment Intro'}
                                                sx={{
                                                    width: { xs: '60%', md: '55%' },
                                                    height: 'auto',
                                                    maxHeight: { xs: 260, md: 'none' },
                                                    objectFit: 'contain',
                                                    borderRadius: '12px',
                                                    display: 'block',
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography variant="h3" style={{ color: '#C91C6E', fontWeight: 700, textAlign: 'center', fontSize: 'clamp(22px, 3.5vw, 34px)', lineHeight: 1.3 }}>
                                                {currentQuestion.title || 'Assessment Introduction'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {currentQuestion.content && (
                                        <Typography variant="body1" style={{ color: 'var(--brand-text-muted, #555)', fontSize: '17px', lineHeight: 1.6, textAlign: 'center' }}>
                                            {currentQuestion.content}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ maxWidth: '1160px', margin: '0 auto', width: '100%' }}>
                                    {/* Same question header as the Assessment Preview page: number + subtitle/question + circular progress */}
                                    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: { xs: 2, sm: 3 } }}>
                                        {/* Question number */}
                                        <Box sx={{
                                            flexShrink: 0, textAlign: 'center',
                                            pr: { xs: 2, sm: 3 }, borderRight: '2px solid var(--brand-border, #E5E7EB)',
                                            display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                Question
                                            </Typography>
                                            <Typography sx={{ fontSize: { xs: 34, sm: 44 }, fontWeight: 800, lineHeight: 1, color: ACCENT }}>
                                                {String(realQuestionNumber).padStart(2, '0')}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                                of {realQuestionTotal}
                                            </Typography>
                                        </Box>

                                        {/* Subtitle + question */}
                                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                            {currentQuestion.question_subtitle_top && (
                                                <Typography sx={{
                                                    display: 'inline-block', alignSelf: 'flex-start',
                                                    fontSize: 16, color: ACCENT, fontWeight: 600, textAlign: 'left',
                                                    backgroundColor: `color-mix(in srgb, ${ACCENT} 12%, transparent)`,
                                                    borderRadius: '6px', px: 1.25, py: 0.5, mb: 0.75,
                                                }}>
                                                    {currentQuestion.question_subtitle_top}
                                                </Typography>
                                            )}
                                            {currentQuestion.instrument_question && (
                                                <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, color: 'var(--brand-text, #111827)', lineHeight: 1.3, textAlign: 'left' }}>
                                                    {renderTextSegments(processedQuestionSegments)}
                                                </Typography>
                                            )}
                                            {currentQuestion.question_subtitle_bottom && (
                                                <Typography sx={{ fontSize: 16,fontWeight: 600, color: '#666666ff', mt: 1, textAlign: 'left' }}>
                                                    {currentQuestion.question_subtitle_bottom}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Right: circular progress with raaz icon */}
                                        <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box sx={{ position: 'relative', width: 76, height: 76 }}>
                                                <CircularProgress variant="determinate" value={100} size={76} thickness={3.5}
                                                    sx={{ color: 'var(--brand-border, #F0F0F0)', position: 'absolute', left: 0, top: 0 }} />
                                                <CircularProgress variant="determinate" value={calculateProgress()} size={76} thickness={3.5}
                                                    sx={{ color: ACCENT, position: 'absolute', left: 0, top: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <RazProgressSVG color="#ED6628" style={{ width: 42, height: 42 }} />
                                                </Box>
                                            </Box>
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: ACCENT, mt: 0.5 }}>
                                                {Math.round(calculateProgress())}%
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {renderOptions()}
                                </Box>
                            )
                        )}

                        {!currentQuestion && total === 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ color: 'var(--brand-text-muted, #aaa)' }}>No {assessType}-assessment questions.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right nav — outside card on sm+ only */}
                {total > 0 && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', pl: 1.5, flexShrink: 0 }}>
                        <IconButton className={classes.navButton}
                            onClick={() => setCurrentIdx((p) => Math.min(total - 1, p + 1))}
                            disabled={currentIdx === total - 1 || isNextDisabled()}>
                            <ArrowForwardIosIcon className={classes.navIcon} />
                        </IconButton>
                    </Box>
                )}
            </Box>

        </Box>
    );
};

export default AssessmentTab;
